import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Filter, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { formatCurrency, formatDateTime, cn } from '@/lib/utils'
import type { Transaction } from '@/types'

export default function History() {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [filterSymbol, setFilterSymbol] = useState('')
  const [filterType, setFilterType] = useState<'' | 'buy' | 'sell'>('')

  const load = async () => {
    setIsLoading(true)
    const res = await api.getTransactions({
      page,
      limit: 25,
      symbol: filterSymbol || undefined,
      type: filterType || undefined,
    })

    if (res.success && res.data) {
      setTransactions(res.data)
      setTotalPages(res.meta?.totalPages ?? 1)
      setTotal(res.meta?.total ?? 0)
    }

    setIsLoading(false)
  }

  useEffect(() => { load() }, [page, filterSymbol, filterType])

  const handleFilterChange = () => {
    setPage(1)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Transaction History</h1>
        <p className="text-white/40 text-sm mt-0.5">All your past trades</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-white/30">
          <Filter className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider font-medium">Filter</span>
        </div>
        <input
          type="text"
          value={filterSymbol}
          onChange={(e) => { setFilterSymbol(e.target.value.toUpperCase()); handleFilterChange() }}
          placeholder="Symbol"
          className="input-base w-32 uppercase"
        />
        <div className="flex gap-1">
          {(['', 'buy', 'sell'] as const).map((type) => (
            <button
              key={type}
              onClick={() => { setFilterType(type); handleFilterChange() }}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize',
                filterType === type
                  ? type === 'buy' ? 'bg-gain/15 text-gain' : type === 'sell' ? 'bg-loss/15 text-loss' : 'bg-white/10 text-white'
                  : 'text-white/30 hover:text-white hover:bg-white/[0.05]'
              )}
            >
              {type || 'All'}
            </button>
          ))}
        </div>
        {total > 0 && (
          <span className="ml-auto text-xs text-white/30">{total.toLocaleString()} transactions</span>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-14 bg-white/[0.04] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-12 text-center text-white/30 text-sm">
            No transactions found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-white/[0.06]">
                  <tr>
                    {['Date', 'Symbol', 'Type', 'Shares', 'Price', 'Total'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-white/30 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-4 text-sm text-white/50">
                        {formatDateTime(tx.timestamp)}
                      </td>
                      <td className="px-4 py-4">
                        <Link
                          to={`/trade/${tx.symbol}`}
                          className="font-semibold text-sm hover:text-accent transition-colors"
                        >
                          {tx.symbol}
                        </Link>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={cn(
                            'text-xs font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider',
                            tx.type === 'buy' ? 'bg-gain/10 text-gain' : 'bg-loss/10 text-loss'
                          )}
                        >
                          {tx.type}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-mono text-sm">{tx.shares.toLocaleString()}</td>
                      <td className="px-4 py-4 font-mono text-sm text-white/60">
                        {formatCurrency(tx.price)}
                      </td>
                      <td className="px-4 py-4 font-mono text-sm font-medium">
                        {formatCurrency(tx.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 disabled:opacity-30"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <span className="text-xs text-white/30">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 disabled:opacity-30"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
